import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { createCategory } from "../features/categories/categoriesSlice";

/**
 * Модалка создания категории — открывается поверх формы товара, чтобы не уходить
 * на /warehouse/categories/new и не терять заполненную форму.
 *
 * Обязательное поле одно — название (оно же unique на бэкенде). slug генерируется
 * сервером из названия. onCreated({id, name}) вызывается после успешного POST.
 */
export default function CategoryCreateModal({ open, initialName, onCancel, onCreated }) {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Каждое открытие — чистая форма с предзаполненным поисковым запросом.
  useEffect(() => {
    if (!open) return;
    setName(String(initialName || "").trim());
    setDescription("");
    setFormError(null);
    setSaving(false);
  }, [open, initialName]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      // stopImmediatePropagation, а не stopPropagation: слушатель родительской модалки
      // висит на том же window, и обычный stopPropagation его не остановил бы —
      // Escape закрыл бы заодно и форму товара со всем введённым.
      e.stopImmediatePropagation();
      onCancel();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [open, onCancel]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (saving) return;

    const trimmedName = String(name || "").trim();
    if (!trimmedName) {
      setFormError("Название обязательно");
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const created = await dispatch(
        createCategory({ body: { name: trimmedName, description: String(description || "").trim() } })
      ).unwrap();
      onCreated({ id: created.id, name: created.name || trimmedName });
    } catch (err) {
      const msg =
        (typeof err === "string" && err) ||
        err?.name?.[0] ||
        err?.detail ||
        err?.non_field_errors?.[0] ||
        "Не удалось создать категорию";
      setFormError(String(msg));
      setSaving(false);
    }
  };

  // Портал в body: иначе <form> этой модалки оказался бы вложен в <form> модалки
  // товара — невалидный HTML, внутреннюю форму браузер выбрасывает и onSubmit
  // не срабатывает. Заодно оверлей не наследует stacking context родителя.
  return createPortal(
    <div
      style={styles.overlay}
      onMouseDown={(e) => {
        e.stopPropagation();
        onCancel();
      }}
    >
      <div style={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>Новая категория</div>
          <button type="button" onClick={onCancel} style={styles.modalClose} title="Закрыть">
            ×
          </button>
        </div>

        <form style={styles.modalBody} onSubmit={handleSubmit}>
          {formError && <div style={styles.error}>{formError}</div>}

          <div style={styles.field}>
            <label style={styles.label}>Название *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Название категории"
              style={styles.input}
              autoFocus
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Описание</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Необязательно"
              style={styles.textarea}
              rows={3}
            />
          </div>

          <div style={styles.modalActions}>
            <button type="button" onClick={onCancel} style={styles.cancelBtn} disabled={saving}>
              Отмена
            </button>
            <button type="submit" style={styles.confirmBtn} disabled={saving}>
              {saving ? "Создаю…" : "Создать"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    // Выше родительской модалки товара (1000), иначе уедет под неё.
    zIndex: 1100,
  },
  modal: {
    width: "min(460px, 92vw)",
    maxHeight: "90vh",
    background: "var(--c-card)",
    border: "1px solid var(--c-border-light)",
    borderRadius: 16,
    boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  modalTitle: { fontSize: 17, fontWeight: 700, color: "var(--c-text)" },
  modalClose: {
    width: 30,
    height: 30,
    borderRadius: 16,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontSize: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalBody: { padding: 20, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 },
  label: { fontSize: 14, fontWeight: 600, color: "var(--c-text-muted)" },
  input: {
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    fontFamily: "inherit",
  },
  textarea: {
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    resize: "vertical",
    fontFamily: "inherit",
  },
  error: {
    padding: "10px 12px",
    borderRadius: 8,
    background: "var(--c-danger-dim)",
    color: "var(--c-danger)",
    border: "1px solid var(--c-danger)",
    fontSize: 15,
    fontWeight: 700,
  },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 },
  confirmBtn: {
    padding: "9px 20px",
    borderRadius: 99,
    border: "none",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 15,
  },
  cancelBtn: {
    padding: "9px 20px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 15,
  },
};
